import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  Avatar,
  Tabs,
  Tab,
  TablePagination,
  Slider,
} from '@mui/material';
import {
  ExpandMore,
  Payment,
  ArrowBack,
  Refresh,
  AccountBalance,
  Add,
  Description,
  ContentCopy,
  Share,
  PictureAsPdf,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { getDebitsByBorrowers, updateIndividualDebitStatus, convertDebitToInterest, getDebitHistory } from '../../services/request/Debits';
import { getBorrowers } from '../../services/request/Borrowers';
import { createdPayment, getPaymentsByDebit, getAllPayments } from '../../services/request/Payments';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { BorrowerAPIItem } from '../../models/LendingModels';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DebitCaptureModal from '../admin/outstandings/DebitCaptureChild';
import AmountInput from '../../shared-components/amount-input/AmountInput';

interface DebitItem {
  id: string;
  amount: number;
  outstanding: number;
  interestRate: number;
  durationType: string;
  startDate: string;
  endDate: string;
  paymentDate: number;
  emiAmount: number;
  emiDate: string;
  status: string;
  type?: string;
  conversionComment?: string;
}

const CustomerDebitsView: React.FC = () => {
  const { customerId } = useParams<{ customerId: string }>();
  
  const [customer, setCustomer] = useState<BorrowerAPIItem | null>(null);
  const [debits, setDebits] = useState<DebitItem[]>([]);
  const [totalOutstanding, setTotalOutstanding] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedDebit, setExpandedDebit] = useState<string | null>(null);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedDebitId, setSelectedDebitId] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentType: '',
    amount: '',
    note: '',
    paymentReceivedBy: '',
    creditScore: '' as string,
  });
  const [payments, setPayments] = useState<{ [key: string]: any[] }>({});
  const [loadingPayments, setLoadingPayments] = useState<{ [key: string]: boolean }>({});
  const [updatingStatus, setUpdatingStatus] = useState<{ [key: string]: boolean }>({});
  const [tabValue, setTabValue] = useState(0);
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [loadingAllPayments, setLoadingAllPayments] = useState(false);
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);
  const [openCaptureDebitDialog, setOpenCaptureDebitDialog] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('ACTIVE');
  const [debitPage, setDebitPage] = useState(0);
  const [debitRowsPerPage, setDebitRowsPerPage] = useState(20);
  const [totalDebits, setTotalDebits] = useState(0);
  const [reportPreviewOpen, setReportPreviewOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [convertDebitId, setConvertDebitId] = useState<string | null>(null);
  const [convertDebitAmount, setConvertDebitAmount] = useState<number | null>(null);
  const [convertForm, setConvertForm] = useState({
    interestRate: '',
    conversionComment: '',
    startDate: '',
    endDate: '',
    paymentDate: '',
  });
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    if (customerId) {
      fetchCustomerData();
      fetchDebits();
    }
  }, [customerId]);

  useEffect(() => {
    if (customerId && tabValue === 0) {
      fetchDebits();
    }
  }, [statusFilter, debitPage, debitRowsPerPage, tabValue]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchAllPayments();
    }
  }, [tabValue, paymentPage, paymentRowsPerPage]);

  const fetchCustomerData = async () => {
    try {
      const response = await getBorrowers(0, 100, {});
      if (response.code === 200) {
        const found = response.result.find((b: BorrowerAPIItem) => b.id === customerId);
        if (found) {
          setCustomer(found);
        }
      }
    } catch (err) {
      console.error('Error fetching customer:', err);
    }
  };

  const fetchDebits = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Build filter object - only include status if not 'ALL'
      const filterObj: any = {};
      if (statusFilter && statusFilter !== 'ALL') {
        filterObj.status = statusFilter;
      }
      
      const response = await getDebitsByBorrowers(customerId, debitPage, debitRowsPerPage, filterObj);
      
      if (response.data && response.data.code === 200) {
        setDebits(response.data.result || []);
        // Set total outstanding from debit-insights API
        setTotalOutstanding(response.data.totalOutstanding || 0);
        // Set total count for pagination
        setTotalDebits(response.data.totalCount || response.data.result?.length || 0);
      } else {
        setDebits([]);
        setTotalOutstanding(0);
        setTotalDebits(0);
      }
    } catch (err) {
      setError('Failed to fetch debits');
      console.error('Error fetching debits:', err);
      setDebits([]);
      setTotalDebits(0);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentsForDebit = async (debitId: string) => {
    try {
      setLoadingPayments(prev => ({ ...prev, [debitId]: true }));
      const response = await getPaymentsByDebit(debitId, 0, 50);
      
      if (response.data && response.data.code === 200) {
        setPayments(prev => ({ ...prev, [debitId]: response.data.result || [] }));
      }
    } catch (err) {
      console.error('Error fetching payments:', err);
    } finally {
      setLoadingPayments(prev => ({ ...prev, [debitId]: false }));
    }
  };

  const handleDebitExpand = (debitId: string) => {
    const isExpanding = expandedDebit !== debitId;
    setExpandedDebit(isExpanding ? debitId : null);
    
    if (isExpanding && !payments[debitId]) {
      fetchPaymentsForDebit(debitId);
    }
  };

  const handleOpenPaymentDialog = (debitId: string) => {
    setSelectedDebitId(debitId);
    setPaymentForm({
      paymentType: '',
      amount: '',
      note: '',
      paymentReceivedBy: '',
      creditScore: '',
    });
    setOpenPaymentDialog(true);
  };

  const handleClosePaymentDialog = () => {
    setOpenPaymentDialog(false);
    setSelectedDebitId(null);
    setPaymentForm({
      paymentType: '',
      amount: '',
      note: '',
      paymentReceivedBy: '',
      creditScore: '',
    });
  };

  const handlePaymentFormChange = (e: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
    const { name, value } = e.target;
    setPaymentForm(prev => ({ ...prev, [name as string]: value }));
  };

  const handleSubmitPayment = async () => {
    if (!selectedDebitId) return;

    // Validation
    if (!paymentForm.paymentType) {
      toast.error('Please select a payment type');
      return;
    }

    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      setLoading(true);
      const payload: Record<string, string | number> = {
        paymentType: paymentForm.paymentType,
        amount: paymentForm.amount,
        note: paymentForm.note,
        paymentReceivedBy: paymentForm.paymentReceivedBy,
      };
      if (paymentForm.creditScore !== '' && paymentForm.creditScore !== undefined) {
        payload.creditScore = Number(paymentForm.creditScore);
      }
      const response = await createdPayment(selectedDebitId, payload);

      if (response.status === 200 && response.data.paymentId) {
        toast.success('Payment captured successfully! 🎉');
        handleClosePaymentDialog();
        // Refresh debits and payments
        await fetchDebits();
        await fetchPaymentsForDebit(selectedDebitId);
      } else {
        toast.error('Failed to capture payment');
      }
    } catch (err) {
      toast.error('Error capturing payment');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (debitId: string, newStatus: string, currentStatus: string) => {
    if (newStatus === currentStatus) return;

    try {
      setUpdatingStatus(prev => ({ ...prev, [debitId]: true }));
      const response = await updateIndividualDebitStatus(debitId, newStatus);

      if (response.status === 200 || (response.data && response.data.code === 200)) {
        toast.success(`Status updated to ${newStatus} successfully! ✅`);
        // Refresh debits list
        await fetchDebits();
      } else {
        toast.error('Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
      console.error('Error:', err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [debitId]: false }));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return 'info';
      case 'CLOSED': return 'default';
      case 'CREATED': return 'warning';
      case 'ACTIVE': return 'success';
      case 'INACTIVE': return 'error';
      case 'COMPLETED': return 'success';
      default: return 'default';
    }
  };

  const calculateProgress = (outstanding: number, amount: number) => {
    if (amount === 0) return 0;
    return ((amount - outstanding) / amount) * 100;
  };

  const fetchAllPayments = async () => {
    try {
      setLoadingAllPayments(true);
      const response = await getAllPayments(paymentPage, paymentRowsPerPage);
      
      if (response.data && response.data.code === 200) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      } else if (response.status === 200 && response.data) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error('Error fetching all payments:', err);
      toast.error('Failed to fetch payments');
    } finally {
      setLoadingAllPayments(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPaymentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentRowsPerPage(parseInt(event.target.value, 10));
    setPaymentPage(0);
  };

  const handleOpenCaptureDebitDialog = () => {
    setOpenCaptureDebitDialog(true);
  };

  const handleCloseCaptureDebitDialog = () => {
    setOpenCaptureDebitDialog(false);
  };

  const handleDebitCreated = () => {
    // Refresh the debits list after debit is created
    fetchDebits();
    toast.success('Debit added successfully! 🎉');
  };

  const handleStatusFilterChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>) => {
    setStatusFilter(event.target.value as string);
    setDebitPage(0); // Reset to first page when filter changes
  };

  const handleOpenConvertDialog = (debit: DebitItem) => {
    setConvertDebitId(debit.id);
    setConvertDebitAmount(debit.amount || 0);
    setConvertForm({
      interestRate: debit.interestRate ? String(debit.interestRate) : '',
      conversionComment: debit.conversionComment || '',
      startDate: debit.startDate ? String(debit.startDate).substring(0, 10) : '',
      endDate: debit.endDate ? String(debit.endDate).substring(0, 10) : '',
      paymentDate: debit.paymentDate ? String(debit.paymentDate) : '',
    });
    setConvertDialogOpen(true);
  };

  const handleCloseConvertDialog = () => {
    setConvertDialogOpen(false);
    setConvertDebitId(null);
    setConvertDebitAmount(null);
    setConvertForm({
      interestRate: '',
      conversionComment: '',
      startDate: '',
      endDate: '',
      paymentDate: '',
    });
  };

  const handleConvertFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setConvertForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitConvert = async () => {
    if (!convertDebitId) return;
    const rate = parseFloat(convertForm.interestRate);
    if (isNaN(rate) || rate <= 0) {
      toast.error('Please enter a valid interest rate');
      return;
    }

    try {
      const payload: { interestRate: number; conversionComment?: string; startDate?: string; endDate?: string; paymentDate?: number } = {
        interestRate: rate,
      };
      if (convertForm.conversionComment) {
        payload.conversionComment = convertForm.conversionComment;
      }
      if (convertForm.startDate) {
        payload.startDate = convertForm.startDate;
      }
       if (convertForm.endDate) {
         payload.endDate = convertForm.endDate;
       }
       if (convertForm.paymentDate) {
         const paymentDay = parseInt(convertForm.paymentDate, 10);
         if (!isNaN(paymentDay)) {
           payload.paymentDate = paymentDay;
         }
       }

      const response = await convertDebitToInterest(convertDebitId, payload);
      if (response.status === 200 && response.data && response.data.code === 200) {
        toast.success('Debit converted to interest successfully! 🎉');
      } else {
        toast.success('Debit converted to interest successfully! 🎉');
      }
      handleCloseConvertDialog();
      await fetchDebits();
    } catch (err) {
      console.error('Error converting debit:', err);
      toast.error('Failed to convert debit. Please try again.');
    }
  };

  const handleOpenHistory = async (debitId: string) => {
    setHistoryDialogOpen(true);
    setHistoryLoading(true);
    try {
      const response = await getDebitHistory(debitId);
      const events = response.result || response || [];
      setHistoryEvents(events);
    } catch (err) {
      console.error('Error fetching debit history:', err);
      toast.error('Failed to load debit history');
      setHistoryEvents([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDebitPageChange = (event: unknown, newPage: number) => {
    setDebitPage(newPage);
  };

  const handleDebitRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDebitRowsPerPage(parseInt(event.target.value, 10));
    setDebitPage(0); // Reset to first page when page size changes
  };

  const buildOutstandingReportText = () => {
    if (!customer) {
      return 'No customer selected.';
    }

    if (!debits.length) {
      return `No outstanding debits found for ${customer.name}.`;
    }

    let totalAmount = 0;
    let totalOutstandingLocal = 0;
    let nearestDueDate: Date | null = null;
    const interestRates = new Set<number>();

    debits.forEach((debit) => {
      totalAmount += debit.amount || 0;
      totalOutstandingLocal += debit.outstanding || 0;

      if (debit.endDate) {
        const due = new Date(debit.endDate);
        if (!nearestDueDate || due < nearestDueDate) {
          nearestDueDate = due;
        }
      }

      if (typeof debit.interestRate === 'number') {
        interestRates.add(debit.interestRate);
      }
    });

    const lines: string[] = [];
    lines.push(`Customer Outstanding Report - ${customer.name}`);
    lines.push('');
    lines.push(`Total Debit Amount: ${formatCurrency(totalAmount)}`);
    lines.push(`Current Outstanding: ${formatCurrency(totalOutstandingLocal)}`);

    const nearestDueDateStr = nearestDueDate
      ? (nearestDueDate as Date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : 'Not available';

    lines.push(`Nearest Due Date: ${nearestDueDateStr}`);

    if (interestRates.size > 0) {
      const rates = Array.from(interestRates)
        .sort((a, b) => a - b)
        .map((r) => `${r}%`)
        .join(', ');
      lines.push(`Interest Rate(s): ${rates}`);
    } else {
      lines.push('Interest Rate(s): Not available');
    }

    lines.push('');
    lines.push('Debit-wise breakdown:');
    debits.forEach((debit, index) => {
      const dueStr = debit.endDate
        ? new Date(debit.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : 'Not set';
      lines.push(
        `${index + 1}. Amount: ${formatCurrency(debit.amount)}, ` +
          `Outstanding: ${formatCurrency(debit.outstanding)}, ` +
          `Due Date: ${dueStr}, Interest Rate: ${debit.interestRate ?? 'N/A'}%`
      );
    });

    return lines.join('\n');
  };

  const handleOpenOutstandingReportPreview = () => {
    const text = buildOutstandingReportText();
    setReportText(text);
    setReportPreviewOpen(true);
  };

  const handleCopyOutstandingReport = async () => {
    try {
      const text = reportText || buildOutstandingReportText();
      setReportText(text);
      await navigator.clipboard.writeText(text);
      toast.success('Outstanding report copied to clipboard!', { position: 'bottom-center' });
    } catch (err) {
      console.error('Failed to copy report:', err);
      toast.error('Failed to copy report. Please try again.', { position: 'bottom-center' });
    }
  };

  const handleShareOutstandingReport = async () => {
    try {
      const text = reportText || buildOutstandingReportText();
      setReportText(text);

      if (navigator.share) {
        await navigator.share({
          title: `Customer Outstanding Report - ${customer?.name ?? ''}`,
          text,
        });
        toast.success('Outstanding report shared!', { position: 'bottom-center' });
      } else {
        await navigator.clipboard.writeText(text);
        toast.info('Share not available. Report copied to clipboard instead.', {
          position: 'bottom-center',
        });
      }
    } catch (err: any) {
      if (err && err.name === 'AbortError') {
        return;
      }

      console.error('Failed to share report:', err);
      try {
        const text = buildOutstandingReportText();
        setReportText(text);
        await navigator.clipboard.writeText(text);
        toast.info('Share failed. Report copied to clipboard instead.', {
          position: 'bottom-center',
        });
      } catch {
        toast.error('Failed to share or copy report. Please try again.', {
          position: 'bottom-center',
        });
      }
    }
  };

  const handleDownloadOutstandingReportPdf = () => {
    if (!customer || !debits.length) {
      toast.error('No data to export', { position: 'bottom-center' });
      return;
    }

    const formatAmountForPdf = (value: number) =>
      `INR ${value.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;

    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text(`Customer Outstanding Report - ${customer.name}`, 14, 18);

    doc.setFontSize(10);
    const totalAmount = debits.reduce((sum, d) => sum + (d.amount || 0), 0);
    const totalOutstandingLocal = debits.reduce((sum, d) => sum + (d.outstanding || 0), 0);
    const summaryLines = [
      `Total Debit Amount: ${formatAmountForPdf(totalAmount)}`,
      `Current Outstanding: ${formatAmountForPdf(totalOutstandingLocal)}`,
    ];
    doc.text(summaryLines, 14, 26);

    const body = debits.map((debit, index) => [
      index + 1,
      (debit.id || '').substring(0, 8),
      formatAmountForPdf(debit.amount),
      formatAmountForPdf(debit.outstanding),
      debit.endDate ? new Date(debit.endDate).toLocaleDateString('en-IN') : 'Not set',
      debit.interestRate != null ? `${debit.interestRate}%` : 'N/A',
      debit.status || 'N/A',
    ]);

    autoTable(doc, {
      head: [
        ['#', 'Debit ID', 'Total Amount', 'Outstanding', 'Due Date', 'Interest Rate', 'Status'],
      ],
      body,
      startY: 34,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [25, 118, 210] },
    });

    const fileName = `outstanding_report_${customer.name.replace(/\s+/g, '_')}.pdf`;
    doc.save(fileName);
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default' }}>
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => window.close()} color="primary">
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1">
              Customer Debits
            </Typography>
            {customer && (
              <Typography variant="body2" color="text.secondary">
                {customer.name} - ID: {customer.id.substring(0, 8)}...
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCaptureDebitDialog}
            color="primary"
          >
            Add Debit
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchDebits}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Customer Summary Card */}
      {customer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                    {customer.name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{customer.name}</Typography>
                    <Chip 
                      label={customer.creditStatus} 
                      color={customer.creditStatus === 'GOOD' ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Total Outstanding</Typography>
                <Typography variant="h5" color="error">
                  {formatCurrency(totalOutstanding)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  From Debit Insights API
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Total Debits</Typography>
                <Typography variant="h5">
                  {debits.length}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">Comments</Typography>
                <Typography variant="body1">
                  {customer.comments || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    gap: 1,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Description />}
                    onClick={handleOpenOutstandingReportPreview}
                  >
                    Outstanding Report
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="secondary"
                    startIcon={<PictureAsPdf />}
                    onClick={handleDownloadOutstandingReportPdf}
                  >
                    Download PDF
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="All Debits" />
          <Tab label="Payment History" />
        </Tabs>
      </Box>

      {/* Tab Panel: Debits */}
      {tabValue === 0 && (
        <Box>
          {/* Status Filter */}
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={statusFilter}
                label="Filter by Status"
                onChange={handleStatusFilterChange as any}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                <MenuItem value="ACTIVE">Active (default)</MenuItem>
                <MenuItem value="OPEN">Open</MenuItem>
                <MenuItem value="CLOSED">Closed</MenuItem>
                <MenuItem value="CREATED">Created</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
              </Select>
            </FormControl>
            <Typography variant="body2" color="text.secondary">
              Showing {debits.length} of {totalDebits} debits
            </Typography>
          </Box>

          {debits.length === 0 && !loading ? (
            <Alert severity="info">
              {totalDebits === 0 
                ? 'No debits found for this customer.'
                : `No debits found with status "${statusFilter}".`}
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {debits.map((debit) => (
            <Accordion 
              key={debit.id}
              expanded={expandedDebit === debit.id}
              onChange={() => handleDebitExpand(debit.id)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="h6" color="primary">
                        {formatCurrency(debit.amount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Outstanding</Typography>
                      <Typography variant="h6" color="error">
                        {formatCurrency(debit.outstanding)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
                      <Typography variant="body1">
                        {debit.interestRate}%
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">EMI</Typography>
                      <Typography variant="body1">
                        {formatCurrency(debit.emiAmount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">
                        {debit.durationType}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary">Payment Date</Typography>
                      <Typography variant="body1" fontWeight="bold" color="warning.main">
                        Day {debit.paymentDate}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <Chip 
                      label={debit.status} 
                      color={getStatusColor(debit.status) as any}
                      size="small"
                    />
                  </Grid>
                  {debit.type === 'SHORT_TERM_DEBIT' && (
                    <Grid item xs={12} md={2}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleOpenConvertDialog(debit)}
                      >
                        Convert to Interest
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => handleOpenHistory(debit.id)}
                    >
                      View History
                    </Button>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  {/* Debit Details */}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" gutterBottom>Debit Information</Typography>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Start Date</Typography>
                              <Typography variant="body1">{debit.startDate || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">End Date</Typography>
                              <Typography variant="body1">{debit.endDate || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Box sx={{ 
                                bgcolor: 'warning.light', 
                                p: 2, 
                                borderRadius: 2, 
                                border: '2px solid',
                                borderColor: 'warning.main'
                              }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  Monthly Payment Due Date
                                </Typography>
                                <Typography variant="h5" color="warning.dark" fontWeight="bold">
                                  Day {debit.paymentDate} of every month
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Interest payment should be made on this date
                                </Typography>
                              </Box>
                            </Grid>
                            {debit.type === 'INTEREST' && debit.conversionComment && (
                              <Grid item xs={12}>
                                <Box sx={{ mt: 2 }}>
                                  <Typography variant="body2" color="text.secondary">
                                    Conversion Comment
                                  </Typography>
                                  <Typography variant="body1">
                                    {debit.conversionComment}
                                  </Typography>
                                </Box>
                              </Grid>
                            )}
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">EMI Date</Typography>
                              <Typography variant="body1">{debit.emiDate || 'N/A'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider sx={{ my: 1 }} />
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Update Debit Status
                              </Typography>
                              <FormControl fullWidth size="small">
                                <Select
                                  value={debit.status}
                                  onChange={(e) => handleStatusUpdate(debit.id, e.target.value, debit.status)}
                                  disabled={updatingStatus[debit.id]}
                                  sx={{ bgcolor: 'background.paper' }}
                                >
                                  <MenuItem value="OPEN">Open</MenuItem>
                                  <MenuItem value="CLOSED">Closed</MenuItem>
                                  <MenuItem value="CREATED">Created</MenuItem>
                                  <MenuItem value="ACTIVE">Active</MenuItem>
                                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                                  <MenuItem value="COMPLETED">Completed</MenuItem>
                                </Select>
                              </FormControl>
                              {updatingStatus[debit.id] && (
                                <LinearProgress sx={{ mt: 1 }} />
                              )}
                            </Grid>
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                Payment Progress
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ flexGrow: 1 }}>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={calculateProgress(debit.outstanding, debit.amount)}
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>
                                <Typography variant="body2">
                                  {calculateProgress(debit.outstanding, debit.amount).toFixed(1)}%
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Capture Payment</Typography>
                            <AccountBalance color="primary" />
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Record a new payment for this debit
                          </Typography>
                          <Button
                            variant="contained"
                            fullWidth
                            startIcon={<Payment />}
                            onClick={() => handleOpenPaymentDialog(debit.id)}
                            size="large"
                          >
                            Add Payment
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  {/* Payment History */}
                  <Box>
                    <Typography variant="h6" gutterBottom>Payment History</Typography>
                    {loadingPayments[debit.id] ? (
                      <LinearProgress />
                    ) : payments[debit.id] && payments[debit.id].length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Amount</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Received By</TableCell>
                              <TableCell>Note</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {payments[debit.id].map((payment, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  {payment.createdDate ? new Date(payment.createdDate).toLocaleDateString() : 
                                   payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="success.main" fontWeight="bold">
                                    {formatCurrency(payment.amount || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip label={payment.paymentType || 'N/A'} size="small" />
                                </TableCell>
                                <TableCell>{payment.paymentReceivedBy || 'N/A'}</TableCell>
                                <TableCell>{payment.note || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">No payments recorded yet for this debit.</Alert>
                    )}
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
              ))}
              </Box>
              {/* Pagination Controls */}
              {totalDebits > 0 && (
                <Paper sx={{ mt: 2 }}>
                  <TablePagination
                    component="div"
                    count={totalDebits}
                    page={debitPage}
                    onPageChange={handleDebitPageChange}
                    rowsPerPage={debitRowsPerPage}
                    onRowsPerPageChange={handleDebitRowsPerPageChange}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                  />
                </Paper>
              )}
            </>
          )}
        </Box>
      )}

      {/* Tab Panel: All Payment History */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">All Payment History</Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchAllPayments}
              disabled={loadingAllPayments}
              size="small"
            >
              Refresh
            </Button>
          </Box>

          {loadingAllPayments ? (
            <LinearProgress sx={{ mb: 2 }} />
          ) : allPayments.length === 0 ? (
            <Alert severity="info">
              No payment records found.
            </Alert>
          ) : (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Debit ID</TableCell>
                      <TableCell>Received By</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allPayments.map((payment) => (
                      <TableRow key={payment.id || payment.paymentId} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {(payment.id || payment.paymentId || '').substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {payment.createdDate ? new Date(payment.createdDate).toLocaleDateString() : 
                           payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {formatCurrency(payment.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={payment.paymentType || 'N/A'} 
                            size="small"
                            color={
                              payment.paymentType === 'REPAYMENT' ? 'success' : 
                              payment.paymentType === 'TOP_UP' ? 'info' : 
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {(payment.debitId || 'N/A').substring(0, 8)}...
                          </Typography>
                        </TableCell>
                        <TableCell>{payment.paymentReceivedBy || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {payment.note || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalPayments}
                page={paymentPage}
                onPageChange={handleChangePage}
                rowsPerPage={paymentRowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Payment Capture Dialog */}
      <Dialog
        open={openPaymentDialog}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Capture Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth required>
              <InputLabel>Payment Type</InputLabel>
              <Select
                name="paymentType"
                value={paymentForm.paymentType}
                label="Payment Type"
                onChange={handlePaymentFormChange as any}
              >
                <MenuItem value="TOP_UP">Topup Credit</MenuItem>
                <MenuItem value="REPAYMENT">Repayment</MenuItem>
                <MenuItem value="INTEREST">Interest Payment</MenuItem>
              </Select>
            </FormControl>
            <AmountInput
              fullWidth
              label="Amount"
              name="amount"
              value={paymentForm.amount}
              onChange={handlePaymentFormChange}
              placeholder="Enter payment amount"
              required
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              fullWidth
              label="Note"
              name="note"
              value={paymentForm.note}
              onChange={handlePaymentFormChange}
              placeholder="Add any additional notes"
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              label="Received By"
              name="paymentReceivedBy"
              value={paymentForm.paymentReceivedBy}
              onChange={handlePaymentFormChange}
              placeholder="Enter receiver name"
            />
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Credit score (0 = worst, 5 = good)
              </Typography>
              <Slider
                value={paymentForm.creditScore === '' ? 0 : Number(paymentForm.creditScore)}
                min={0}
                max={5}
                step={1}
                marks={[
                  { value: 0, label: '0' },
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                  { value: 5, label: '5' },
                ]}
                valueLabelDisplay="auto"
                onChange={(_, value) => setPaymentForm(prev => ({ ...prev, creditScore: String(Array.isArray(value) ? value[0] : value) }))}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            color="warning"
            onClick={handleClosePaymentDialog} 
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleSubmitPayment}
            disabled={loading || !paymentForm.amount || !paymentForm.paymentType}
          >
            Submit Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Outstanding Report Preview Dialog */}
      <Dialog
        open={reportPreviewOpen}
        onClose={() => setReportPreviewOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Customer Outstanding Report</DialogTitle>
        <DialogContent dividers>
          <TextField
            value={reportText}
            onChange={() => {}}
            multiline
            fullWidth
            minRows={10}
            InputProps={{ readOnly: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<ContentCopy />}
            onClick={handleCopyOutstandingReport}
          >
            Copy
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Share />}
            onClick={handleShareOutstandingReport}
          >
            Share
          </Button>
          <Button onClick={() => setReportPreviewOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Short-Term Debit to Interest Dialog */}
      <Dialog
        open={convertDialogOpen}
        onClose={handleCloseConvertDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Convert to Interest Debit</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Interest Rate (%)"
              name="interestRate"
              type="number"
              value={convertForm.interestRate}
              onChange={handleConvertFormChange}
              fullWidth
              inputProps={{ step: '0.1', min: '0' }}
            />
            <TextField
              label="New Start Date"
              name="startDate"
              type="date"
              value={convertForm.startDate}
              onChange={handleConvertFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: adjust start date for interest calculation"
            />
            <TextField
              label="New End Date"
              name="endDate"
              type="date"
              value={convertForm.endDate}
              onChange={handleConvertFormChange}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Optional: adjust end date"
            />
            <TextField
              label="Payment Date (1-31)"
              name="paymentDate"
              type="number"
              value={convertForm.paymentDate}
              onChange={handleConvertFormChange}
              fullWidth
              inputProps={{ min: '1', max: '31' }}
              helperText="Optional: monthly payment day"
            />
            {convertDebitAmount !== null && convertForm.interestRate && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Estimated Monthly Interest
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(
                    (convertDebitAmount * parseFloat(convertForm.interestRate || '0')) / 100
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Formula: (amount / 100) × interest %
                </Typography>
              </Box>
            )}
            <TextField
              label="Conversion Comment"
              name="conversionComment"
              value={convertForm.conversionComment}
              onChange={handleConvertFormChange}
              fullWidth
              multiline
              rows={3}
              placeholder="Reason for converting short-term debit to interest debit"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConvertDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitConvert}>
            Convert
          </Button>
        </DialogActions>
      </Dialog>

      {/* Debit History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Debit History</DialogTitle>
        <DialogContent dividers>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <LinearProgress sx={{ width: '100%' }} />
            </Box>
          ) : historyEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No history available for this debit.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {historyEvents.map((event, index) => (
                <Box key={index} sx={{ p: 1.5, borderRadius: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" fontWeight="bold">
                    {event.description}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {event.eventDate
                      ? new Date(event.eventDate).toLocaleString()
                      : 'Date not available'}
                  </Typography>
                  {event.amount != null && (
                    <Typography variant="body2">
                      Amount: {formatCurrency(event.amount)}
                    </Typography>
                  )}
                  {event.paymentType && (
                    <Typography variant="caption" color="text.secondary">
                      Payment Type: {event.paymentType}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Capture Debit Modal */}
      {customerId && (
        <DebitCaptureModal
          open={openCaptureDebitDialog}
          borrowerId={customerId}
          handleDebitModelClose={handleCloseCaptureDebitDialog}
          onDebitCreated={handleDebitCreated}
        />
      )}
    </Box>
  );
};

export default CustomerDebitsView;

